import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, ExternalLink, CheckCircle2, AlertTriangle, Clock, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

const Card = ({ label, value, tone = "default", testid }) => {
  const tones = {
    default: "border-white/10 text-white",
    green: "border-[#00FF88]/40 text-[#00FF88]",
    amber: "border-amber-500/40 text-amber-300",
    red: "border-red-500/40 text-red-300",
    violet: "border-violet-500/40 text-violet-300",
  };
  return (
    <div className={`rounded-2xl border bg-white/[0.02] p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
    </div>
  );
};

const Chip = ({ status }) => {
  const map = {
    Overdue: "bg-red-500/15 text-red-300 border-red-500/40",
    "Awaiting Repayment": "bg-amber-500/10 text-amber-300 border-amber-500/30",
    Repaid: "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${map[status] || "bg-white/5 text-white/55 border-white/10"}`}>
      {status === "Overdue" && <AlertTriangle className="w-3 h-3" />}
      {status === "Awaiting Repayment" && <Clock className="w-3 h-3" />}
      {status === "Repaid" && <CheckCircle2 className="w-3 h-3" />}
      {status || "—"}
    </span>
  );
};

const PilotSnapshot = ({ token, onMarkedRepaid }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [linkDraft, setLinkDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [savingLink, setSavingLink] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snap, sett] = await Promise.all([
        axios.get(`${API}/pilot/snapshot`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/pilot/settings`, { headers: { "X-Admin-Token": token } }),
      ]);
      setData(snap.data);
      setSettings(sett.data);
      setLinkDraft(sett.data?.repayment_link || "");
    } catch (e) {
      toast.error("Could not load Founder Snapshot.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const saveLink = async () => {
    if (!/^https?:\/\//i.test(linkDraft.trim())) {
      toast.error("Repayment link must be a valid URL.");
      return;
    }
    setSavingLink(true);
    try {
      const { data: s } = await axios.put(
        `${API}/pilot/settings`,
        { repayment_link: linkDraft.trim() },
        { headers: { "X-Admin-Token": token } }
      );
      setSettings(s);
      toast.success("Universal repayment link saved.");
    } catch {
      toast.error("Failed to save link.");
    } finally {
      setSavingLink(false);
    }
  };

  const markRepaid = async (row) => {
    if (!window.confirm(`Mark ${row.name} as Repaid? This sets repayment date to today and updates the rate.`)) return;
    setBusyId(row.id);
    try {
      await axios.post(`${API}/pilot/${row.id}/mark-repaid`, {}, { headers: { "X-Admin-Token": token } });
      toast.success(`${row.name} marked Repaid.`);
      await load();
      onMarkedRepaid?.();
    } catch {
      toast.error("Failed to mark repaid.");
    } finally {
      setBusyId(null);
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const { data: r } = await axios.post(
        `${API}/pilot/auto-reminders/run`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Auto reminders sent — reminder:${r.reminder} · due:${r.due} · overdue:${r.overdue}`);
      await load();
    } catch {
      toast.error("Could not run auto reminders.");
    } finally {
      setRunningReminders(false);
    }
  };

  const downloadTracker = async (kind) => {
    try {
      const res = await axios.get(`${API}/pilot/tracker.${kind}`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gigafloat-tracker.${kind}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Founder tracker ${kind.toUpperCase()} downloaded.`);
    } catch {
      toast.error(`Could not download ${kind.toUpperCase()}.`);
    }
  };

  if (loading && !data) {
    return <div className="py-20 text-center text-white/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }
  if (!data) return null;

  const p = data.pilot_snapshot;
  const f = data.financial_snapshot;
  const queue = data.repayment_queue || [];

  return (
    <div data-testid="pilot-snapshot">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">// Founder Float Tracker</div>
          <div className="font-display font-extrabold text-2xl mt-1">Daily snapshot</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => downloadTracker("csv")}
            className="inline-flex items-center gap-2 text-xs font-bold border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/10 rounded-full px-4 py-2 transition-all"
            data-testid="pilot-tracker-csv"
          >
            <Download className="w-3.5 h-3.5" /> Tracker CSV
          </button>
          <button
            onClick={() => downloadTracker("xlsx")}
            className="inline-flex items-center gap-2 text-xs font-bold bg-[#00FF88] text-black hover:opacity-90 rounded-full px-4 py-2 transition-all btn-glow"
            data-testid="pilot-tracker-xlsx"
          >
            <Download className="w-3.5 h-3.5" /> Tracker Excel
          </button>
          <button
            onClick={runReminders}
            disabled={runningReminders}
            className="inline-flex items-center gap-2 text-xs font-bold border border-white/15 hover:border-[#00FF88]/40 text-white/80 hover:text-[#00FF88] rounded-full px-4 py-2 transition-all disabled:opacity-50"
            data-testid="pilot-run-auto-reminders"
            title="Send reminder/due/overdue emails to all eligible funded participants now"
          >
            {runningReminders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Run auto reminders
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 text-xs font-bold border border-white/10 hover:border-white/30 text-white/70 hover:text-white rounded-full px-4 py-2 transition-all"
            data-testid="pilot-snapshot-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Settings */}
      <div className="rounded-2xl border border-[#00FF88]/20 bg-[#00FF88]/[0.03] p-5 mb-6">
        <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88] mb-2">// Universal repayment link · used by all emails</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            value={linkDraft}
            onChange={(e) => setLinkDraft(e.target.value)}
            placeholder="https://square.link/u/..."
            className="flex-1 min-w-0 px-4 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-xl text-white font-mono-c text-sm placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50"
            data-testid="pilot-square-link-input"
          />
          <button
            onClick={saveLink}
            disabled={savingLink || linkDraft.trim() === (settings?.repayment_link || "")}
            className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-xl btn-glow disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="pilot-square-link-save"
          >
            {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          {settings?.repayment_link && (
            <a
              href={settings.repayment_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/15 text-white/70 hover:text-white hover:border-white/30 font-bold text-sm px-4 py-2 rounded-xl"
              data-testid="pilot-square-link-open"
            >
              <ExternalLink className="w-4 h-4" /> Test
            </a>
          )}
        </div>
      </div>

      {/* Pilot Snapshot */}
      <div className="mb-5">
        <div className="text-[11px] font-mono-c uppercase tracking-widest text-white/40 mb-2">Pilot snapshot</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <Card label="Total apps" value={p.total_applications} testid="snap-total-applications" />
          <Card label="Approved" value={p.approved} tone="green" testid="snap-approved" />
          <Card label="Waitlisted" value={p.waitlisted || 0} tone="green" testid="snap-waitlisted" />
          <Card label="Denied" value={p.denied} tone="red" testid="snap-denied" />
          <Card label="Funded" value={p.funded} tone="violet" testid="snap-funded" />
          <Card label="Awaiting" value={p.awaiting_repayment} tone="amber" testid="snap-awaiting" />
          <Card label="Repaid" value={p.repaid} tone="green" testid="snap-repaid" />
          <Card label="Overdue" value={p.overdue} tone="red" testid="snap-overdue" />
        </div>
      </div>

      {/* Financial Snapshot */}
      <div className="mb-6">
        <div className="text-[11px] font-mono-c uppercase tracking-widest text-white/40 mb-2">Financial snapshot</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Total funded" value={fmtMoney(f.total_funded)} testid="snap-total-funded" />
          <Card label="Total repaid" value={fmtMoney(f.total_repaid)} tone="green" testid="snap-total-repaid" />
          <Card label="Outstanding" value={fmtMoney(f.outstanding_balance)} tone="amber" testid="snap-outstanding" />
          <Card label="Repayment rate" value={`${f.repayment_rate_pct}%`} tone={f.repayment_rate_pct >= 80 ? "green" : "amber"} testid="snap-rate" />
        </div>
      </div>

      {/* Repayment Queue */}
      <div className="rounded-2xl border border-white/8 overflow-hidden">
        <div className="px-5 py-3 bg-white/[0.03] border-b border-white/8 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88]">// Repayment queue</div>
            <div className="text-sm text-white/55">Sorted by overdue first, then soonest due</div>
          </div>
          <div className="text-xs text-white/40 font-mono-c">{queue.length} entries</div>
        </div>
        {queue.length === 0 ? (
          <div className="py-14 text-center text-white/40 text-sm">No funded participants yet. Mark someone as Funded to populate the queue.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pilot-queue-table">
              <thead className="bg-white/[0.02] text-[10px] font-mono-c uppercase tracking-widest text-white/45">
                <tr>
                  <th className="text-left px-4 py-3">Name / email</th>
                  <th className="text-left px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Funded</th>
                  <th className="text-left px-4 py-3">Due</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Days</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {queue.map((row) => {
                  const isOverdue = row.repayment_status === "Overdue";
                  const isRepaid = row.repayment_status === "Repaid";
                  return (
                    <tr
                      key={row.id}
                      className={`border-t border-white/5 hover:bg-white/[0.02] ${isOverdue ? "bg-red-500/[0.04]" : ""}`}
                      data-testid={`pilot-queue-row-${row.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-white font-semibold">{row.name || "—"}</div>
                        <div className="text-xs text-white/45 font-mono-c">{row.email}</div>
                      </td>
                      <td className="px-4 py-3 text-white">{fmtMoney(row.amount_funded)}</td>
                      <td className="px-4 py-3 text-white/70 font-mono-c text-xs">{fmtDate(row.funding_date)}</td>
                      <td className="px-4 py-3 text-white/70 font-mono-c text-xs">{fmtDate(row.due_date)}</td>
                      <td className="px-4 py-3"><Chip status={row.repayment_status} /></td>
                      <td className={`px-4 py-3 font-mono-c text-xs ${isOverdue ? "text-red-300 font-bold" : "text-white/55"}`}>
                        {row.days_until_due === null || row.days_until_due === undefined
                          ? "—"
                          : row.days_until_due > 0
                            ? `in ${row.days_until_due}d`
                            : row.days_until_due === 0
                              ? "today"
                              : `${Math.abs(row.days_until_due)}d late`}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!isRepaid && (
                          <button
                            onClick={() => markRepaid(row)}
                            disabled={busyId === row.id}
                            className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#00FF88] text-black px-3 py-1.5 rounded-full hover:opacity-90 disabled:opacity-50"
                            data-testid={`pilot-mark-repaid-${row.id}`}
                          >
                            {busyId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Mark repaid
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PilotSnapshot;
